/**
 * UnauthenticatedToolAccessErrorBlock - Shown when an unauthenticated user
 * triggers a tool that requires auth. Displays the backend error message and
 * a sign-up button that opens the sign-up modal.
 */

import { showSignUpModalAtom } from '@/atoms/chatAtoms';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { useTrackSignUpWallViewed } from '@/hooks/useTrackSignUpWallViewed';
import { EvidenceSignUpWall } from '@/models/analytics/features/evidence';
import { useTranslations } from '@heidi/runtime/i18n';
import { useSetAtom } from 'jotai';
import { Lock } from 'lucide-react';
import { useEffect, useRef, type JSX } from 'react';

import { formatToolName } from '@heidi/evidence/data';

export interface UnauthenticatedToolAccessErrorBlockProps {
  /** Error message from the backend (e.g. "Authentication required. Please sign up to use this feature.") */
  message: string;
  /** Raw tool identifier from the backend (e.g. get_appointments, create_clinical_task). Displayed via formatToolName. */
  toolName?: string;
  /** Additional CSS classes */
  className?: string;
}

export const UnauthenticatedToolAccessErrorBlock = ({
  message,
  toolName,
  className,
}: UnauthenticatedToolAccessErrorBlockProps): JSX.Element => {
  const { t } = useTranslations();
  const trackSignUpWallViewed = useTrackSignUpWallViewed();
  const setShowSignUpModal = useSetAtom(showSignUpModalAtom);
  const { trackChatError } = useEvidenceAnalytics();
  const errorTrackedRef = useRef(false);
  const displayName = toolName ? formatToolName(toolName) : null;

  useEffect(() => {
    if (!errorTrackedRef.current) {
      errorTrackedRef.current = true;
      trackChatError({
        errorType: 'unauthenticated_tool',
        errorMessage: message,
      });
    }
  }, [message, trackChatError]);

  return (
    <div
      className={cn(
        'flex w-full items-center gap-4 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
        'transition-all duration-200',
        className
      )}
      data-testid="unauthenticated-tool-access-error-block"
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <Lock
          className="size-5 shrink-0 text-validation-destructive"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {displayName && (
            <TypographyP2 className="truncate font-medium text-text-primary">
              {displayName}
            </TypographyP2>
          )}
          <TypographyCaption className="break-words text-validation-destructive">
            {message}
          </TypographyCaption>
        </div>
      </div>
      <Button
        variant="default"
        size="md"
        onClick={() => {
          trackSignUpWallViewed(EvidenceSignUpWall.ToolAccessWall);
          setShowSignUpModal(true);
        }}
        data-testid="unauthenticated-tool-access-signup-button"
        className="shrink-0"
      >
        {t('chat.unauthenticatedToolAccess.signUp')}
      </Button>
    </div>
  );
};
